import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Trash2, Bot, Sparkles, Mic, MicOff, MessageSquare, HelpCircle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const FAQ_ITEMS = [
  { q: "What can the AI Assistant do?", a: "The AI Assistant can answer questions about your daily schedule, revenue, staff workload, client trends, and business insights. It has access to your real appointment data, services, and invoices." },
  { q: "Can it create or modify appointments?", a: "Not yet — currently the AI is read-only. It can tell you about your schedule but you'll need to make changes through the app. We're working on adding action capabilities soon." },
  { q: "Is my data safe?", a: "Yes. The AI only accesses your business data within Apptio. Conversations are stored securely and only visible to your business admins. We use OpenAI's GPT-4o with enterprise-grade security." },
  { q: "What kind of questions work best?", a: "Try questions like:\n• \"What's my schedule today?\"\n• \"How much revenue did we make this week?\"\n• \"Who's the busiest staff member?\"\n• \"Any gaps in tomorrow's bookings?\"\n• \"Top services by revenue this month\"" },
  { q: "Does voice input work?", a: "Yes! Tap the microphone button and speak your question. It works in Chrome, Safari, and Edge. Make sure you've allowed microphone access in your browser." },
  { q: "Why is this Premium only?", a: "The AI Assistant uses advanced language models that have per-query costs. It's included as a Premium feature to provide the best possible experience for our premium subscribers." },
  { q: "Can I use it in Vietnamese?", a: "Yes! The AI responds in the same language you write in. Try asking in Vietnamese and it will respond in Vietnamese." },
];

function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="space-y-2">
      {FAQ_ITEMS.map((item, i) => (
        <div key={i} className="border border-[#E5E7E1] rounded-xl overflow-hidden">
          <button onClick={() => setOpenIdx(openIdx === i ? null : i)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F5F7F0] transition-colors">
            <span className="text-sm font-medium text-[#1A2421]">{item.q}</span>
            <ChevronDown size={16} className={`text-[#5C6656] transition-transform flex-shrink-0 ml-2 ${openIdx === i ? 'rotate-180' : ''}`} />
          </button>
          {openIdx === i && <div className="px-4 pb-3 text-xs text-[#5C6656] leading-relaxed whitespace-pre-wrap">{item.a}</div>}
        </div>
      ))}
    </div>
  );
}

export default function AIAssistantPage() {
  const [tab, setTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => `page-${Date.now()}`);
  const [recording, setRecording] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 100);
  }, []);

  useEffect(() => {
    if (tab !== 'chat') return;
    axios.get(`${API}/ai-assistant/history?session_id=${sessionId}`, { withCredentials: true })
      .then(r => { if (r.data.messages?.length) { setMessages(r.data.messages); scrollToBottom(); } })
      .catch(() => {});
  }, [sessionId, scrollToBottom, tab]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const recognition = new SR();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-GB';
      recognition.onresult = (e) => { const t = e.results[0]?.[0]?.transcript || ''; if (t) setInput(prev => prev ? prev + ' ' + t : t); setRecording(false); };
      recognition.onerror = () => setRecording(false);
      recognition.onend = () => setRecording(false);
      recognitionRef.current = recognition;
    }
  }, []);

  const toggleVoice = () => {
    if (!recognitionRef.current) { toast.error('Voice not supported'); return; }
    if (recording) { recognitionRef.current.stop(); setRecording(false); }
    else { try { recognitionRef.current.start(); setRecording(true); } catch { setRecording(false); } }
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    scrollToBottom();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai-assistant/chat`, { message: msg, session_id: sessionId }, { withCredentials: true });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      scrollToBottom();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed';
      if (detail.includes('Premium')) toast.error('AI Assistant is a Premium feature. Upgrade to access.');
      else setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Try again.' }]);
    }
    setLoading(false);
  };

  const clearChat = () => {
    axios.delete(`${API}/ai-assistant/history?session_id=${sessionId}`, { withCredentials: true }).catch(() => {});
    setMessages([]);
    setSessionId(`page-${Date.now()}`);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] lg:h-screen max-w-3xl mx-auto" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7E1] flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#556B2F] to-[#3E6641] flex items-center justify-center"><Sparkles size={16} className="text-white" /></div>
          <div><h1 className="text-base font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>AI Assistant</h1><p className="text-[10px] text-[#5C6656]">Premium feature</p></div>
        </div>
        <div className="flex items-center gap-1">
          {/* Tabs */}
          <div className="flex bg-[#F5F7F0] rounded-lg p-0.5 mr-2">
            <button onClick={() => setTab('chat')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'chat' ? 'bg-white text-[#1A2421] shadow-sm' : 'text-[#5C6656]'}`} data-testid="tab-chat">
              <MessageSquare size={13} className="inline mr-1" />Chat
            </button>
            <button onClick={() => setTab('help')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'help' ? 'bg-white text-[#1A2421] shadow-sm' : 'text-[#5C6656]'}`} data-testid="tab-help">
              <HelpCircle size={13} className="inline mr-1" />How it works
            </button>
          </div>
          {tab === 'chat' && messages.length > 0 && (
            <button onClick={clearChat} className="p-2 rounded-lg hover:bg-[#F5F7F0] text-[#5C6656]" data-testid="clear-chat"><Trash2 size={16} /></button>
          )}
        </div>
      </div>

      {/* Help tab */}
      {tab === 'help' && (
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="max-w-lg mx-auto space-y-6">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#556B2F]/10 to-[#3E6641]/10 flex items-center justify-center mx-auto mb-3"><Sparkles size={24} className="text-[#556B2F]" /></div>
              <h2 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Your salon's AI assistant</h2>
              <p className="text-sm text-[#5C6656] mt-1">Ask questions in plain English (or Vietnamese) and get instant answers about your business.</p>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { icon: '📅', label: 'Schedule', desc: 'Daily appointments & gaps' },
                { icon: '💰', label: 'Revenue', desc: 'Sales & trends' },
                { icon: '👥', label: 'Staff', desc: 'Workload & performance' },
              ].map((f, i) => (
                <div key={i} className="text-center p-3 rounded-xl border border-[#E5E7E1]">
                  <span className="text-xl">{f.icon}</span>
                  <p className="text-xs font-bold text-[#1A2421] mt-1">{f.label}</p>
                  <p className="text-[10px] text-[#5C6656]">{f.desc}</p>
                </div>
              ))}
            </div>
            <h3 className="text-sm font-bold text-[#1A2421]">Frequently Asked Questions</h3>
            <FAQSection />
          </div>
        </div>
      )}

      {/* Chat tab */}
      {tab === 'chat' && (<>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {messages.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <Sparkles size={28} className="text-[#556B2F] mb-3" />
              <p className="text-base font-bold text-[#1A2421] mb-1" style={{ fontFamily: 'Outfit' }}>How can I help?</p>
              <p className="text-xs text-[#5C6656] mb-5 max-w-sm">Ask about revenue and reports, <strong>or tell me to book, reschedule, cancel, and block times</strong>. I can read &amp; write to your calendar.</p>
              <div className="grid grid-cols-2 gap-2 w-full max-w-md">
                {["Any gaps in tomorrow's schedule?", "Revenue summary for this week", "Book Sarah Jane for a Gel Colour Manicure at 3pm today", "Cancel the 2pm appointment today", "Block Emily from 1pm to 2pm for lunch", "Reschedule John's 3pm to 4:30pm"].map((q, i) => (
                  <button key={i} onClick={() => sendMessage(q)} className="text-left text-xs px-3 py-2.5 rounded-xl border border-[#E5E7E1] hover:bg-[#F5F7F0] text-[#5C6656] transition-colors">{q}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && <div className="w-7 h-7 rounded-md bg-[#556B2F] flex items-center justify-center flex-shrink-0 mt-0.5"><Bot size={14} className="text-white" /></div>}
              <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${msg.role === 'user' ? 'bg-[#1A2421] text-white rounded-br-sm' : 'bg-[#F5F7F0] text-[#1A2421] rounded-bl-sm'}`}>{msg.content}</div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-2.5">
              <div className="w-7 h-7 rounded-md bg-[#556B2F] flex items-center justify-center flex-shrink-0"><Bot size={14} className="text-white" /></div>
              <div className="bg-[#F5F7F0] px-4 py-3 rounded-2xl rounded-bl-sm"><div className="flex gap-1"><div className="w-1.5 h-1.5 rounded-full bg-[#556B2F] animate-bounce" style={{animationDelay:'0ms'}} /><div className="w-1.5 h-1.5 rounded-full bg-[#556B2F] animate-bounce" style={{animationDelay:'150ms'}} /><div className="w-1.5 h-1.5 rounded-full bg-[#556B2F] animate-bounce" style={{animationDelay:'300ms'}} /></div></div>
            </div>
          )}
        </div>
        <div className="flex-shrink-0 px-4 py-3 border-t border-[#E5E7E1]" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' }}>
          <div className="flex items-center gap-2">
            <button onClick={toggleVoice} className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${recording ? 'bg-[#E2725B] text-white animate-pulse' : 'hover:bg-[#F5F7F0] text-[#5C6656]'}`}>{recording ? <MicOff size={18} /> : <Mic size={18} />}</button>
            <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder={recording ? 'Listening...' : 'Ask anything...'} className="flex-1 h-11 px-4 rounded-xl border border-[#E5E7E1] text-sm focus:outline-none focus:ring-2 focus:ring-[#556B2F]/20 bg-[#FAFAF8]" disabled={loading} />
            <Button onClick={() => sendMessage()} disabled={!input.trim() || loading} className="h-11 w-11 rounded-xl bg-[#1A2421] hover:bg-[#0D1210] text-white p-0 disabled:opacity-30"><Send size={16} /></Button>
          </div>
        </div>
      </>)}
    </div>
  );
}
